import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTeamTransactions } from '../services/transactionService';
import { loadTeamTransactionTasks } from '../services/transactionTasks';
import NicheNewsSection from './NicheNewsSection';

// Marketing Manager home: every team deal grouped by stage (each links to the deal), plus her open tasks.
const STAGES = [
  { key: 'coming', title: 'Coming Soon' },
  { key: 'live', title: 'Just Listed / Active' },
  { key: 'uc', title: 'Under Contract' },
  { key: 'closing', title: 'Closing Soon (next 14 days)' },
  { key: 'closed', title: 'Closed / Just Sold' },
];

const closingDateOf = (t) => t.closingDate || t.anticipatedClosingDate || t.closeDate || '';
const stageOf = (t) => {
  const s = String(t.status || t.listingStatus || '').toLowerCase();
  if (s === 'lost' || s === 'withdrawn' || s === 'cancelled') return null;
  if (s.includes('closed') || s.includes('sold')) return 'closed';
  if (s.includes('coming')) return 'coming';
  if (s.includes('under') || s.includes('pending') || s.includes('contract')) {
    const d = closingDateOf(t) ? new Date(closingDateOf(t)) : null;
    if (d && !isNaN(d) && (d - Date.now()) / 86400000 <= 14) return 'closing';
    return 'uc';
  }
  return 'live';
};
const addressOf = (t) => t.propertyAddress || t.desiredLocation || [t.firstName, t.lastName].filter(Boolean).join(' ') || 'Untitled deal';
const fmtDate = (d) => { if (!d) return ''; const x = new Date(d.length === 10 ? d + 'T12:00:00' : d); return isNaN(x) ? d : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

function MarketingManagerDashboard({ currentUser }) {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.uid) return;
    let alive = true;
    Promise.all([getTeamTransactions(user.uid, userRole), loadTeamTransactionTasks(user.uid, userRole).catch(() => [])])
      .then(([txs, rows]) => {
        if (!alive) return;
        setDeals(txs || []);
        const open = (rows || []).filter((t) => t.status !== 'completed' && !t.completedAt);
        open.sort((a, b) => String(a.dueDate || '9999').localeCompare(String(b.dueDate || '9999')));
        setTasks(open);
      })
      .catch((err) => { console.error('Could not load deals', err); if (alive) { setError('Could not load deals.'); setDeals([]); } });
    return () => { alive = false; };
  }, [user?.uid, userRole]);

  const byId = Object.fromEntries((deals || []).map((d) => [d.id, d]));
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Welcome, {currentUser?.firstName || 'there'}!</h1>
      <p style={s.sub}>Team listings and deals by stage, plus your marketing to-dos.</p>
      {error && <p style={{ color: '#b00020' }}>{error}</p>}

      <NicheNewsSection uid={user?.uid} />

      <section style={s.card}>
        <div style={s.cardHead}>
          <h2 style={s.h2}>My open tasks</h2>
          <button type="button" style={s.linkBtn} onClick={() => navigate('/tasks')}>All tasks</button>
        </div>
        {deals === null ? <p style={s.muted}>Loading...</p> : tasks.length === 0 ? <p style={s.muted}>No open marketing tasks.</p> : (
          <ul style={s.list}>
            {tasks.slice(0, 15).map((t) => (
              <li key={`${t.transactionId}-${t.id}`} style={s.row}>
                <button type="button" style={s.rowBtn} onClick={() => navigate(`/transactions/${t.transactionId}`)}>
                  <span style={{ fontWeight: 600 }}>{t.name || t.title}</span>
                  <span style={s.meta}>
                    {addressOf(byId[t.transactionId] || {})}
                    {t.dueDate && <span style={{ color: t.dueDate < today ? '#b00020' : '#6b6b6b' }}> · due {fmtDate(t.dueDate)}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {STAGES.map((st) => {
        const list = (deals || []).filter((d) => stageOf(d) === st.key);
        return (
          <section key={st.key} style={s.card}>
            <h2 style={s.h2}>{st.title}</h2>
            {deals === null ? <p style={s.muted}>Loading...</p> : list.length === 0 ? <p style={s.muted}>None right now.</p> : (
              <ul style={s.list}>
                {list.map((d) => (
                  <li key={d.id} style={s.row}>
                    <button type="button" style={s.rowBtn} onClick={() => navigate(`/transactions/${d.id}`)}>
                      <span style={{ fontWeight: 600 }}>{addressOf(d)}</span>
                      <span style={s.meta}>{[d.type, d.city, closingDateOf(d) && `closing ${fmtDate(closingDateOf(d))}`].filter(Boolean).join(' · ')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

const s = {
  page: { padding: '2rem', maxWidth: 900, fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: '#1a1a1a' },
  h1: { margin: '0 0 4px', fontSize: 28 },
  sub: { margin: '0 0 20px', color: '#6b6b6b' },
  card: { border: '1px solid #e5e0d0', borderTop: '3px solid #D4AF37', borderRadius: 10, padding: '16px 18px', marginBottom: 16, background: '#fff' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  h2: { margin: '0 0 10px', fontSize: 18 },
  muted: { color: '#8a8a8a', margin: 0 },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  row: { borderTop: '1px solid #f0ece0' },
  rowBtn: { width: '100%', minHeight: 44, textAlign: 'left', background: 'none', border: 'none', padding: '10px 2px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, color: '#1a1a1a', fontSize: 15 },
  meta: { color: '#6b6b6b', fontSize: 13 },
  linkBtn: { background: 'none', border: '1px solid #D4AF37', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', minHeight: 36 },
};

export default MarketingManagerDashboard;
